import { compact } from 'lodash';
import { useMemo } from 'react';

import {
  SelectInputOption,
  TypeaheadSelectInputOption,
} from '@/components/form/baseInputs/inputTypes';
import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { DocumentType } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { useRegenerateSummaryModalFormDataQuery } from '../graphql/RegenerateSummaryModal.generated';
import { getDocumentSummaryPromptDocumentTypes } from '../utils/convertDocumentType';

export function useSummaryOptions(
  documentType: DocumentType,
  currentSummaryId?: string
): {
  summaryOptions: TypeaheadSelectInputOption<string>[];
  promptOptions: TypeaheadSelectInputOption<string>[];
} {
  const { householdId } = useHouseholdDetailsContext();
  const { data } = useRegenerateSummaryModalFormDataQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      householdId: householdId ?? '',
      documentType,
      documentSummaryPromptDocumentTypes:
        getDocumentSummaryPromptDocumentTypes(documentType),
    },
    skip: !householdId,
  });

  const summaryOptions = useMemo(() => {
    const documents = getNodes(data?.documents);

    return compact(
      documents.flatMap((doc) => {
        return compact(
          doc.summaries?.map((summary) => {
            // don't allow formatting like the current summary!
            if (summary.id === currentSummaryId) {
              return null;
            }
            return {
              value: summary.id,
              display: doc.name,
              secondaryText: summary.displayName,
            };
          })
        );
      })
    );
  }, [data, currentSummaryId]);

  const promptOptions: SelectInputOption<string>[] = useMemo(() => {
    const prompts = getNodes(data?.documentSummaryPrompts).sort((a, b) => {
      // order so that default prompts are first, then auto-run prompts
      if (a.isDefaultForDocType && !b.isDefaultForDocType) return -1;
      if (!a.isDefaultForDocType && b.isDefaultForDocType) return 1;
      if (a.shouldAutoRun && !b.shouldAutoRun) return -1;
      if (!a.shouldAutoRun && b.shouldAutoRun) return 1;
      return 0;
    });

    return prompts.map((prompt) => {
      const endAdornment = prompt.shouldAutoRun ? (
        <Badge variant={BadgeVariants.Accent} display="Auto run" />
      ) : undefined;

      return {
        value: prompt.id,
        display: prompt.displayName,
        endAdornment,
      } as SelectInputOption<string>;
    });
  }, [data]);

  return { summaryOptions, promptOptions };
}
